import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { useLocation, useParams } from 'react-router-dom';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { FormWrapper } from 'ksnm-common-ui/lib/Form/FormWrapper';
import type { ReactElement } from 'react';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { cmApi } from '@src/api';
import { usePermission } from '@src/utils/hooks/usePermission';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { organizationsStore } from '@stores/dictionaryStores/organizationsStore';
import { organizationCardStore } from '@stores/dictionaryStores/organizationsStore/organizationCardStore';
import type { CompanyDto, OrganizationDto } from '@src/api';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { FormWithActions } from '../../FormWithActions';
import { OrganizationCardPage } from './OrganizationCardPage';
import { validate } from './validateSchema';

function OrganizationCardComponent(): ReactElement | null {
  const [counterparties, setCounterparties] = useState<CompanyDto[]>([]);

  const { code } = useParams<{ code: string }>();
  const { state: routeState } = useLocation();
  const { push } = useHistory();

  const { organization, save: saveOrganization, setDisableDelete, fetch, setNew, isNew } = organizationCardStore;
  const { organizations } = organizationsStore;

  useEffect(() => fetchReferences(['organizations', 'organizationTypes', 'organizationBusinessTypes']), []);

  useEffect(() => {
    (async () => {
      const { data } = await cmApi.companies.getCompanies({ counterparty: true });

      if (data) {
        setCounterparties(data.map((item) => ({ ...item, id: item.code })));
      }
    })();
  }, []);

  usePermission('CmOrganization', organizationCardStore);

  const initialValues = useMemo(() => {
    const initCompany = counterparties?.find((com) => com.code === organization?.companyCode);
    const additionalInfo = {
      companyLegalAdress: initCompany?.legalAddress,
      companyInn: initCompany?.inn,
      companyKpp: initCompany?.kpp,
      companyFullName: initCompany?.fullName,
    };

    return toJS({ ...organization, ...additionalInfo });
  }, [counterparties, organization]);

  const handleSubmit = useCallback(
    async (values: OrganizationDto) => {
      const validation = validate(routeState?.head || false)(values);

      if (validation) {
        return validation;
      }

      try {
        await saveOrganization({
          ...values,
          head: routeState?.head,
          organizationTypeCode: routeState?.head ? 1 : values.organizationTypeCode,
        });

        push(`${RoutePath.ORGANIZATION_CARD}/${values.code}`, { head: routeState?.head });
        return undefined;
      } catch (err) {
        console.error(err);
      }
    },
    [push, routeState?.head, saveOrganization],
  );

  useEffect(() => {
    setDisableDelete(!(code === NEW_ENTITY_ID || !organizations.some(({ parentCode }) => String(parentCode) === code)));
  }, [code, organizations, setDisableDelete]);

  useEffect(() => {
    if (code === NEW_ENTITY_ID) {
      setNew();
    } else {
      fetch(code);
    }
    return () => setNew(false);
  }, [code, fetch, setNew]);

  const goToOrgs = useCallback(() => push(RoutePath.ORGANIZATIONS), [push]);

  const title = useMemo(
    () =>
      isNew ? 'Новая организационная единица' : organization?.code ? `${organization.code} - ${organization.name}` : '',
    [isNew, organization?.code, organization?.name],
  );

  return (
    <DesignLayout scrollEnable title={title}>
      <FormWrapper layout="vertical" initialValues={initialValues} onSubmit={handleSubmit}>
        <FormWithActions
          store={organizationCardStore}
          goBackLink={goToOrgs}
          withCancelBtn
          exitTooltip="Вернуться в список организаций"
        >
          <OrganizationCardPage counterparties={counterparties} />
        </FormWithActions>
      </FormWrapper>
    </DesignLayout>
  );
}

export const OrganizationCard = observer(OrganizationCardComponent);
